import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { useConsent } from "@/contexts/ConsentContext";
import { ChevronDown, ChevronUp, Shield } from "lucide-react";

export default function CookieBanner() {
  const { consent, bannerVisible, acceptAll, acceptEssential, updateCategories } = useConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  const [marketing, setMarketing] = useState(consent?.marketing ?? false);

  // Restore saved selections when banner is reopened
  useEffect(() => {
    if (bannerVisible && consent) {
      setAnalytics(consent.analytics);
      setMarketing(consent.marketing);
    }
  }, [bannerVisible, consent]);

  if (!bannerVisible) return null;

  const handleSaveSelection = () => {
    updateCategories(analytics, marketing);
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 p-4">
      <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-card shadow-lg">
        <div className="p-5">
          <div className="flex items-start gap-3 mb-4">
            <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display font-bold text-foreground text-sm mb-1">Cookie-Einstellungen</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Wir verwenden Cookies und ähnliche Technologien. Technisch notwendige Cookies sind für den Betrieb erforderlich. Analyse- und Marketing-Cookies werden nur mit Ihrer Zustimmung gesetzt.{" "}
                <Link to="/datenschutz" className="text-primary hover:underline">
                  Datenschutzerklärung
                </Link>
              </p>
            </div>
          </div>

          {showDetails && (
            <div className="mb-4 space-y-3 rounded-xl border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-3">
                <Checkbox id="ck-essential" checked disabled className="opacity-70" />
                <label htmlFor="ck-essential" className="text-sm text-foreground font-medium">
                  Notwendig <span className="text-muted-foreground font-normal">(immer aktiv)</span>
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-7">Sitzungstoken, Cookie-Einstellungen, Sidebar-Status.</p>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="ck-analytics"
                  checked={analytics}
                  onCheckedChange={(v) => setAnalytics(v === true)}
                />
                <label htmlFor="ck-analytics" className="text-sm text-foreground font-medium cursor-pointer">
                  Statistik / Analyse
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-7">Google Analytics 4 — Seitenaufrufe, Verweildauer, Geräteinfo. IP-Anonymisierung aktiv.</p>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="ck-marketing"
                  checked={marketing}
                  onCheckedChange={(v) => setMarketing(v === true)}
                />
                <label htmlFor="ck-marketing" className="text-sm text-foreground font-medium cursor-pointer">
                  Marketing
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-7">Google Ads Conversion-Tracking — Messung der Wirksamkeit von Anzeigen.</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Button onClick={acceptAll} size="sm" className="shrink-0 bg-primary text-primary-foreground">
              Alle akzeptieren
            </Button>
            {showDetails ? (
              <Button onClick={handleSaveSelection} size="sm" variant="secondary" className="shrink-0">
                Auswahl speichern
              </Button>
            ) : null}
            <Button onClick={acceptEssential} size="sm" variant="outline" className="shrink-0">
              Nur notwendige
            </Button>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors ml-auto"
            >
              {showDetails ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {showDetails ? "Weniger" : "Einstellungen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
